/**
 * A query-builder "block" wires together the following query components:
 * - header (e.g. "USERS THAT <DID>")
 * - search-bar input (e.g. "Select event")
 * - dropdown (e.g. event selector, time picker)
 */

import {Component} from 'panel';
import findIndex from 'lodash/findIndex';
import findKey from 'lodash/findKey';
import pick from 'lodash/pick';

import {userDetailsClient} from 'common/report/queries/app-api/user-details-client';
import {userDetailsStore} from 'common/report/stores/user-details';
import {encodeRison} from 'common/report/util/url';
import {filterAttributes, serializeAttributes} from 'common/util';
import {truncateMiddle} from 'common/util/string';
import {replaceByIndex} from 'common/util/array';
import {emitTrackEvent, propagateEvent, scrollIntoViewIfNeeded} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ButtonTheme as ButtonThemes} from 'common/components/button/constants';
import {Visibility} from 'common/components/base/stackable';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {promptSharing} from 'common/widgets/sharing';
import {promptSuperadminOverrideShare} from 'common/report/util/saved-entities';
import {getReportUrl} from 'common/report/util';
import {EntityType, EntityTypeNameSingular, SharableEntityType} from 'common/report/util/saved-entities/types';
import {cohortsStore} from 'common/report/stores/cohorts';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {eventPropertyDefinitionsStore, peoplePropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {Store} from 'common/report/stores/store';
import {currentProjectStore} from 'common/report/stores/project-details';
import {featuresStore} from 'common/report/stores/features';
import {ProjectFeatureFlag} from 'common/report/constants';
import {
  eventPrefix as shareEventPrefix,
  ShareButtonUISource,
  track as shareTrack,
} from 'common/widgets/sharing/tracking';
import {renderDateRangeTooltip} from 'common/widgets/date-range-preset-bar/date-range-tooltip';
import {DATE_RANGE_TYPES} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DisplayContext as DateRangeDisplayContext} from 'common/widgets/date-range-picker/models/types';

import {GetSortedMenuSections} from './properties-menu/types';
import {getCustomEventMenuItem} from './util';
import {ChildMenu} from './child-menu';
import {MAX_LABEL_LENGTH, SELECTOR_TYPE, THEME, TRACKING_EVENTS} from './constants';
import {BreakdownMenu} from './breakdown-menu';
import {CohortsMenu} from './cohorts-menu';
import {EventsMenu} from './events-menu';
import {FilterMenu} from './filter-menu';
import {MultiEventsMenu} from './multi-events-menu';
import {PropertiesMenu} from './properties-menu';
import {TimeMenu} from './time-menu';
import {TimeComparisonMenu} from './time-comparison-menu';
import {SegmentMenu} from './segment-menu';
import css from './stylesheets/index.styl?inline';
import template from './index.jade';

// Static definition of all registered child menus
export const MENU_TYPES = {
  breakdown: BreakdownMenu,
  cohorts: CohortsMenu,
  events: EventsMenu,
  filter: FilterMenu,
  properties: PropertiesMenu,
  segment: SegmentMenu,
  tabbed: ChildMenu, // TODO: deprecate this
  time: TimeMenu,
  'time-comparison': TimeComparisonMenu,
  'multi-events': MultiEventsMenu,
};

enum MODAL_TYPE {
  CustomEvent = `CustomEvent`,
  Sharing = `ShareModal`,
}

const COMPONENT_NAME = `mp-query-block`;

interface QueryBlockState {
  isCreatingNewCustomEvent: boolean;
  isHeaderMenuOpen: boolean;
  isMenuOpen: boolean;
  openedMenuForModal: boolean;
  menuType: string;
  permissions: Set<string>;
  searchFilter: string;
  openModal: MODAL_TYPE | null;
  projectId: number;
  /** @todo: [shawn] Add types for these */
  eventData: any;
  selected: any;
  selectedCustomEventId: string;
  staged: any; // TODO: being used in breakdown-menu, this should be removed
  tooltipData: any;
  keydownProxyEl: any;
  getSortedMenuSections: GetSortedMenuSections;
}
export enum Entity {
  Event = `event`,
  CustomEvent = `customEvent`,
  Cohort = `cohort`,
  UserProperty = `userProperty`,
  EventProperty = `eventProperty`,
  CustomProperty = `customProperty`,
}

export class QueryBlock extends Component<QueryBlockState> {
  private childMenu: ChildMenu<any>;
  private inputElement: any;
  private contextPanelVisibilityTimerId: any;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      type: {type: `string`, enum: Object.keys(MENU_TYPES)},
      'all-data-groups': `boolean`,
      'allow-typecast': `boolean`,
      'apply-button': {type: `string`, description: `Only for properties menu type`},
      'apply-button-disabled': `boolean`,
      'data-group-id': `string`,
      'date-range': {type: `json`, description: `Only for time-comparison menu type`},
      disabled: `boolean`,
      'display-context': {type: `string`, default: null},
      'enable-enhanced-list-filters': `boolean`,
      'enable-workspace-filter-restrictions': `boolean`,
      'disable-value-selection': `boolean`,
      'exclude-autotrack-events': {
        type: `boolean`,
        description: `whether to hide autotrack events`,
      },
      'exclude-custom-events': `boolean`,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
      },
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'excluded-elements': {type: `json`, default: []},
      'event-names': {type: `json`, default: null},
      'header-segments': `json`,
      'header-text': `string`,
      'help-text': `string`,
      'input-only': {
        type: `boolean`,
        description: `Set to ignore the selected value from rendering in the block label`,
      },
      'is-blocked-people': `boolean`,
      label: `string`,
      'max-data-history-days': {type: `number`, description: `max days time menu can query into data history`},
      multiple: `boolean`,
      open: `boolean`,
      'prefix-label': `string`,
      'property-combine-mode': {type: `string`, default: null},
      'hide-events': `boolean`,
      'project-has-people': `boolean`,
      'project-has-cohorts': `boolean`,
      'filter-by-types': `json`,
      'project-id': `number`,
      removable: `boolean`,
      'dropdown-alignment': {type: `string`, default: `left`},
      selected: {type: `json`, default: null},
      inverted: `boolean`,
      'selector-icon': `string`,
      'selector-type': {type: `string`, default: SELECTOR_TYPE.TEXT, enum: Object.values(SELECTOR_TYPE)},
      'show-any-event': `boolean`,
      'show-session-event': `boolean`,
      'show-all-events': `boolean`,
      'show-all-tab': `boolean`,
      'show-all-users-cohort': `boolean`,
      'show-top-events': `boolean`,
      'single-data-group': `string`,
      'support-b2b-cohorts': `boolean`,
      'tooltip-data': `json`,
      'inline-cohorts-override': `boolean`,
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
      'show-special-date-property': `boolean`,
      'hide-special-date-property-unit': `boolean`,
      'show-special-dataset-property': `boolean`,
      tabs: `json`,
      theme: {type: `string`, default: THEME.PRIMARY, enum: Object.values(THEME)},
      'track-name': {
        type: `string`,
        description: `If specified, includes "Report name" as a property`,
      },
      'other-group-by-options': {type: `string`, default: `[]`},
      'user-id': `number`,
      'workspace-id': `number`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'search-placeholder': `string`,
      'show-select-all': `boolean`,
      'select-all-disabled': `boolean`,
      'hide-recently-viewed': `boolean`,
      'hide-section-labels': `boolean`,
      'selectable-sensitive-properties': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        eventData: [],
        isCreatingNewCustomEvent: false,
        isHeaderMenuOpen: false,
        isMenuOpen: false,
        openedMenuForModal: false,
        menuType: null,
        permissions: null,
        featureFlags: null,
        searchFilter: ``,
        selected: null,
        staged: null,
        openModal: null,
        projectId: null,
        tooltipData: null,
        keydownProxyEl: null,
        selectedCustomEventId: null,
        getSortedMenuSections: null,
      },

      helpers: {
        filterAttributes,
        propagateEvent: (ev) => propagateEvent(this, ev),
        MODAL_TYPE,

        getSelectorTheme: () => {
          let theme = this.attr(`theme`);
          const isDefaultTheme = !this.hasAttribute(`theme`);
          const selectorType = this.attr(`selector-type`);
          const buttonTheme = findKey(ButtonThemes, (value) => value === theme);

          // validate button theme and use THEME.SECONDARY as a default
          if (selectorType === SELECTOR_TYPE.BUTTON) {
            if (!buttonTheme) {
              throw Error(`Unkown button theme`);
            }

            theme = isDefaultTheme ? THEME.SECONDARY : theme;
          }

          return theme;
        },

        getHeaderSegments: () => {
          if (this.attr(`header-text`)) {
            return [
              {
                type: `text`,
                value: this.attr(`header-text`),
              },
            ];
          }
          return this.attr(`header-segments`) || [];
        },

        handleCancel: () => {
          this.closeMenu();
        },

        handleChildMenuClosedModal: () => {
          if (this.state.openedMenuForModal) {
            this.closeMenu();
          }
        },

        // Handle actions from the context tooltip attached to the query block. First, give the
        // child menu a chance to handle the action. If it's not handled by child menu, then
        // handle the action directly here.
        handleTooltipContextAction: (ev) => {
          const {handled, openedModal} = this.childMenu.handleTooltipContextAction(ev) || {
            handled: false,
            openedModal: false,
          };
          if (!handled) {
            this.helpers.handlePropagatedTooltipContextAction(ev);
          }
          this.update({
            tooltipData: null,
            // If a modal that lives in the child menu is open, then the child menu has to be open.
            // This can happen if you click the edit action on the context panel attached to the query block.
            // This will open the child menu's modal. However, we also have to force open the child menu,
            // otherwise the modal would be hidden.
            ...(openedModal ? {isMenuOpen: true, openedMenuForModal: true} : {}),
          });
        },

        // If the context toolip action is not handled by the child menu, it will be handled here.
        handlePropagatedTooltipContextAction: async (ev) => {
          if (ev.detail.name === `shareCustomEvent`) {
            let allowOverrideShare = true;

            const {eventName, props} = shareTrack.shareButtonClick(ShareButtonUISource.ContextPanel);
            this.track(eventName, props, false, shareEventPrefix);

            if (ev.detail.item.superadminOverrideShare) {
              allowOverrideShare = await promptSuperadminOverrideShare(EntityType.CustomEvent);
            }
            if (allowOverrideShare) {
              this.update({openModal: MODAL_TYPE.Sharing});
              promptSharing(
                {
                  type: SharableEntityType.CustomEvent,
                  id: ev.detail.item.id,
                  'project-id': this.attr(`project-id`),
                },
                this.helpers.handleCloseModal,
              );
            }
          } else if (ev.detail.name === `editCustomEvent`) {
            // TODO handle directly in event menu after moving custom event modal there
            this.helpers.editCustomEvent(ev);
          } else if (ev.detail.name === `editCohort`) {
            // TODO handle directly in properties-menu
            this.helpers.editCohort(ev);
          } else if (ev.detail.name === `viewSampleEvents` && ev.detail.item.url) {
            // TODO handle directly in events-menu
            this.helpers.handleSampleEvent(ev);
          }
        },

        handlePropagatedtooltipContextEditDescription: (ev) => {
          const detail = ev.detail;
          switch (detail.item.type) {
            case EntityTypeNameSingular.Cohort:
              this.updateCohortDescription(detail);
              break;
            case EntityTypeNameSingular.Event:
              this.updateEventDescription(detail);
              break;
            case EntityTypeNameSingular.CustomEvent:
              this.updateEventDescription(detail);
              break;
            case EntityTypeNameSingular.EventProperty:
              this.updatePropertyDescription(detail);
              break;
            case EntityTypeNameSingular.UserProperty:
              this.updatePropertyDescription(detail);
              break;
            case EntityTypeNameSingular.CustomProperty:
              this.updateCustomPropertyDescription(detail);
              break;
            default:
              break;
          }
        },

        editCustomEvent: (ev) => {
          this.update({selectedCustomEventId: ev.detail.item.id});
          this.helpers.clickEditCustomEvent(ev);
        },

        handleSampleEvent: (ev) => {
          this.track(TRACKING_EVENTS.SAMPLE_EVENT, {}, false);

          window.open(ev.detail.item.url, `_blank`);
        },

        editCohort: (ev) => {
          // @ts-ignore - hasCohortEmFeature helper function has many consumers and will go away soon
          // acessing private prop is a lesser evil
          let url = ``;
          if (!currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.DisableEntityManagementCohorts)) {
            const params = {
              cohort_id: ev.detail.item.id,
              include_all_users: true,
              ...(ev.detail.item.dataGroupId
                ? {
                    data_group_id: ev.detail.item.dataGroupId,
                  }
                : {}),
            };
            url = `${getReportUrl(`explore`, {
              projectId: this.attr(`project-id`),
              workspaceId: this.attr(`workspace-id`),
            })}#list/${encodeRison(params)}`;
          } else {
            let urlHash = `#builder/${ev.detail.item.id}`;
            if (ev.detail.item.dataGroupId) {
              urlHash = `${urlHash}/edit/data-group/${ev.detail.item.dataGroupId}`;
            }
            url = `${getReportUrl(`cohorts`, {
              projectId: this.attr(`project-id`),
              workspaceId: this.attr(`workspace-id`),
            })}${urlHash}`;
          }
          window.open(url, `_blank`);
        },

        getSelectedCustomEventId: () => {
          if (this.state.selectedCustomEventId) {
            return this.state.selectedCustomEventId;
          } else {
            return !this.state.isCreatingNewCustomEvent && this.state.selected ? this.state.selected.id : undefined;
          }
        },

        getHeaderMenuSection: (headerSegment) => [
          {
            // format for mp-items-menu
            items: headerSegment.items.map((item) => ({
              ...item,
              isSelected: item.value === headerSegment.selectedItem.value,
              labelTheme: `strong`,
            })),
          },
        ],

        getTooltipPositionParentElement: () => this.el.querySelector(`mp-drop-menu`),

        getChildMenuName: () => this.childMenuComponentName,
        getTheme: () => this.attr(`theme`),
        isReady: () => !!this.state.projectId || [`time`, `time-comparison`].includes(this.state.menuType),
        isReadOnly: () => this.childMenu.canOpenMenu() === false,
        showInput: () => !this.attr(`input-only`),
        menuHasCustomWidth: (): boolean => {
          // note: only relevant for the time menu at the moment
          return this.attr(`type`) === `time`;
        },

        // label config overridden by child menu
        getPrefixLabel: () => this.childMenu.getPrefixLabel(),
        isContentSearchable: () => this.childMenu.isContentSearchable(),
        selectedLabel: () => truncateMiddle(this.childMenu.selectedLabel(), MAX_LABEL_LENGTH),
        selectedIcon: () => this.childMenu.selectedIcon(),

        labelInputInserted: (vnode) => this.update({keydownProxyEl: vnode.elm}),

        handleHeaderSelectClicked: () => {
          this.update({isHeaderMenuOpen: !this.state.isHeaderMenuOpen});
        },

        handleHeaderMenuVisibilityChange: (ev) => {
          if (ev.detail.state !== `open`) {
            this.update({isHeaderMenuOpen: false});
          }
        },

        handleHeaderSelectionChanged: (ev) => {
          this.update({isHeaderMenuOpen: false});
          this.dispatchEvent(new CustomEvent(`select-header-item`, {detail: ev.detail.item}));
        },

        //#region Custom events
        // disable editing if it is an autotrack event

        canWriteCustomEvents: () => {
          const {permissions} = this.state;
          return permissions && permissions.has(`write_customevents`);
        },

        showCreateCustomEvent: () => {
          return (
            this.state.menuType === `events` &&
            this.childMenu.showCreationControls() &&
            !this.attr(`exclude-custom-events`) &&
            this.helpers.canWriteCustomEvents() &&
            !this.attr(`hide-edit-controls`)
          );
        },
        handleCreateCustomEvent: () => {
          this.openCustomEventModal({isCreatingNewCustomEvent: true});
        },
        clickEditCustomEvent: (ev) => {
          ev.stopPropagation(); // do not open the event menu
          this.openCustomEventModal({isCreatingNewCustomEvent: false});
        },
        handleCloseModal: () => {
          this.update({openModal: null, selectedCustomEventId: null});
        },
        deletedCustomEvent: (ev) => {
          const {id} = ev.detail;
          propagateEvent(this, ev);
          const eventData = this.state.eventData.filter((event) => event.id !== id);
          this.update({eventData, openModal: null, selectedCustomEventId: null});
        },
        savedCustomEvent: (ev) => {
          const {customEvent} = ev.detail;
          propagateEvent(this, ev);

          const item = getCustomEventMenuItem(customEvent);
          this.dispatchSelect({item});

          let eventData;
          if (this.state.isCreatingNewCustomEvent) {
            eventData = [...this.state.eventData, item];
          } else {
            const updatedEventIndex = findIndex(this.state.eventData, (event: any) => event.id === customEvent.id);
            eventData = replaceByIndex(this.state.eventData, updatedEventIndex, item);
          }
          this.update({
            eventData,
            openModal: null,
            selectedCustomEventId: null,
          });
        },
        captureTracking: (ev) => {
          const {eventName, props} = ev.detail;
          ev.stopPropagation();
          this.track(eventName, props || {});
        },
        //#endregion

        //#region Custom properties
        // TODO: when we consider permissions for custom properties
        canWriteCustomProperties: () => true,
        handleDeleteCustomProperty: (ev) => this.helpers.handleRemove(ev),
        //#endregion

        preventCloseSelectors: () => {
          return [`mp-context-panel-internal`];
        },

        handleSearchInput: (ev) => this.update({searchFilter: ev.target.value}),
        handleSelect: (ev) => this.dispatchSelect(ev.detail),
        handleLabelClick: () => this.attr(`disabled`) || this.openMenu(),
        handleLabelMouseEnter: () => {
          this.delayShowContextPanel();
        },
        handleLabelMouseLeave: () => {
          this.delayHideContextPanel();
        },
        handleContextPanelHoverIn: () => {
          this.clearContextPanelVisibilityTimer();
        },
        handleContextPanelHoverOut: () => {
          this.delayHideContextPanel();
        },
        handleMenuChange: (ev) => {
          const {state} = ev.detail;

          if (state === Visibility.Closed) {
            this.closeMenu();
          } else if (this.inputElement && this.state.menuType !== `time`) {
            requestAnimationFrame(() => this.inputElement.focus());
          }

          if (state === Visibility.Open) {
            this.dispatchEvent(new CustomEvent(`open`));
            scrollIntoViewIfNeeded(ev.target);
          }
        },

        handleRemove: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(`remove`));
        },

        handleSelectAll: ({detail}) => {
          this.dispatchEvent(new CustomEvent(`selectAll`, {detail}));
        },

        menuInserted: (vnode) => {
          this.childMenu = vnode.elm;
          this.updateSelection();
        },

        searchInputInserted: (vnode) => {
          this.inputElement = vnode.elm;
          this.update({keydownProxyEl: this.inputElement});
        },

        /**
         * Returns all attributes mirrored from this component to the
         * underlying selection menu in a serialized form
         */
        getChildMenuAttributes: () => {
          const [, menuClass] = this.childMenuComponentEntry;
          const attributes = {
            ...pick(this.attrs(), Object.keys(menuClass.attrsSchema)),
            open: this.state.isMenuOpen && this.state.openModal === null,
            'search-filter': this.state.searchFilter,
          };

          return serializeAttributes(attributes);
        },

        renderSelectedDateTooltipIfNeeded: () => {
          const isTimeSelector = this.attr(`type`) === `time`;
          if (isTimeSelector && this.state.selected) {
            const dateRangeClass = DATE_RANGE_TYPES[this.attr(`display-context`) || DateRangeDisplayContext.Default];
            return renderDateRangeTooltip(new dateRangeClass(this.state.selected), currentProjectStore);
          }
          return null;
        },

        /** helpers are this bound, a little helper function that simply re-draws the UI when any store values change */
        updateAfterStoreValueChange: () => this.update(),
      },
    };
  }

  constructor() {
    super();

    this.childMenu = Object.create(ChildMenu.prototype, {});
  }

  get childMenuComponentName() {
    const [type] = this.childMenuComponentEntry;
    return `mp-${type}-menu`;
  }

  getOrganizationId() {
    return userDetailsStore?.value?.projects[this.attr(`project-id`)]?.organization_id;
  }
  // attempts to find a multi selection component
  // first and defaults to type-menu
  get childMenuComponentEntry() {
    const multiple = this.attr(`multiple`);
    let type = this.attr(`type`);

    if (multiple && MENU_TYPES[`multi-${type}`]) {
      type = `multi-${type}`;
    }

    return [type, MENU_TYPES[type]];
  }

  openMenu() {
    if (!this.state.isMenuOpen) {
      this.update({isMenuOpen: true, tooltipData: null});
      this.childMenu.handleLabelClick();
    }
  }

  closeMenu() {
    this.update({
      isMenuOpen: false,
      openedMenuForModal: false,
      searchFilter: ``,
      tooltipData: null,
      staged: null,
    });
    this.childMenu.handleCloseMenu();
    this.dispatchEvent(new CustomEvent(`close`));
  }

  openCustomEventModal({isCreatingNewCustomEvent}) {
    this.update({
      isCreatingNewCustomEvent,
      openModal: MODAL_TYPE.CustomEvent,
    });
  }

  connectedCallback() {
    super.connectedCallback();
    const projectId = this.attr(`project-id`);
    this.update({projectId});
    this.fetchPermissions();

    featuresStore.subscribe(this.helpers.updateAfterStoreValueChange).fetch({projectId});
    userDetailsStore.subscribe(this.helpers.updateAfterStoreValueChange).fetch();
  }

  disconnectedCallback() {
    this.clearContextPanelVisibilityTimer();
    featuresStore.unsubscribe(this.helpers.updateAfterStoreValueChange);
    userDetailsStore.unsubscribe(this.helpers.updateAfterStoreValueChange);
    super.disconnectedCallback();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    switch (attr) {
      case `open`:
        this.update({isMenuOpen: this.attr(`open`)});
        break;
      case `project-id`:
        this.update({projectId: this.attr(`project-id`)});
        break;
      case `selected`:
        this.updateSelection();
        break;
      case `type`:
        if (oldVal !== newVal) {
          this.update({menuType: newVal});
        }
        break;
      default:
        break;
    }
  }

  async fetchPermissions() {
    if (this.state.permissions || !this.attr(`project-id`)) {
      return;
    }

    const userDetails = await userDetailsClient.query({
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });
    const project = userDetails.projects[this.attr(`project-id`)];
    this.update({
      permissions: new Set(project.permissions),
    });
  }

  updateSelection() {
    this.update({selected: this.childMenu.transformSelectedVal(this.attr(`selected`))});
  }

  dispatchSelect(detail) {
    this.dispatchEvent(new CustomEvent(`select`, {detail}));

    if (detail.commit || this.childMenu.shouldCloseOnSelect(detail)) {
      this.closeMenu();
    }
  }

  getSelectedProperty() {
    const {selected} = this.state;
    return selected && selected.length > 0
      ? Object.values(selected[0])[0] // 'segment' format
      : selected; // 'properties' format
  }

  track(event, props, cancelable = true, prefix = COMPONENT_NAME) {
    if (!this.attr(`track-name`)) {
      return;
    }

    emitTrackEvent(this, {
      eventName: event,
      eventPrefix: prefix,
      props: {
        ...props,
        project_id: this.attr(`project-id`),
        tab: this.attr(`track-name`),
      },
      cancelable,
    });
  }

  clearContextPanelVisibilityTimer() {
    if (this.contextPanelVisibilityTimerId) {
      clearTimeout(this.contextPanelVisibilityTimerId);
    }
    this.contextPanelVisibilityTimerId = null;
  }

  async delayShowContextPanel() {
    this.clearContextPanelVisibilityTimer();
    let prefetchedOptions;
    let timerCompleted = false;

    this.contextPanelVisibilityTimerId = setTimeout(() => {
      timerCompleted = true;
      if (this.state.isMenuOpen) {
        return;
      }

      const tooltipData = this.childMenu.getSelectedHoverTooltipData(prefetchedOptions) ?? this.attr(`tooltip-data`);
      this.update({tooltipData});
      if (tooltipData) {
        // Setting cancelable to false is needed to prevent mp-query-multi-block from swallowing the event
        const cancelable = false;
        this.track(
          TRACKING_EVENTS.HOVER_CONTEXT_PANEL,
          {
            'Entity name': tooltipData.header,
            'Query block type': this.attr(`type`),
            'Has description': !!tooltipData.description,
            ...(this.childMenu.getSelectedTrackingProps() || {}),
          },
          cancelable,
        );
      }
    }, 375);

    // Note that this value could change after await
    const currentTimerId = this.contextPanelVisibilityTimerId;
    // Prefetch actions while the delay is running in parallel
    prefetchedOptions = await this.childMenu.prefetchTooltipOptions();

    // Render tooltip from the regular flow if prefetch is done prior or if timer is cancelled
    if (!timerCompleted || this.contextPanelVisibilityTimerId !== currentTimerId) {
      return;
    }

    // Render after timeout with updated data
    const tooltipData = this.childMenu.getSelectedHoverTooltipData(prefetchedOptions) ?? this.attr(`tooltip-data`);
    this.update({tooltipData});
  }

  updateEntityDescriptionAtStore(entityStore: Store<any>, key, description) {
    entityStore.update({
      [key]: {
        ...entityStore.value[key],
        description,
      },
    });
  }

  async undoEntityDescriptionUpdate(entityStore: Store<any>, oldDescription, newDescirption, storeKey, undoRequestObj) {
    try {
      this.updateEntityDescriptionAtStore(entityStore, storeKey, oldDescription);
      await entityStore.patch({
        ...undoRequestObj,
      });
    } catch (err) {
      addToast({message: `Unable to undo the description`, isError: true, type: ToastType.ERROR});
      this.updateEntityDescriptionAtStore(entityStore, storeKey, newDescirption);
    }
  }

  async updateEntityDescription(
    entityStore: Store<any>,
    newDescription,
    storeKey,
    updateRequestObj,
    undoRequestObj,
    entityType,
  ) {
    const descriptionBeforeUpdate = entityStore.value[storeKey].description;
    const cancelable = false;
    this.track(
      TRACKING_EVENTS.EDIT_DESCRIPTION,
      {
        'New description': newDescription,
        'Old description': descriptionBeforeUpdate,
        'Access point': `context panel`,
        'Entity type': entityType,
      },
      cancelable,
    );
    this.updateEntityDescriptionAtStore(entityStore, storeKey, newDescription);
    try {
      await entityStore.patch({
        ...updateRequestObj,
      });
      const undoCallback = () => {
        this.undoEntityDescriptionUpdate(
          entityStore,
          descriptionBeforeUpdate,
          newDescription,
          storeKey,
          undoRequestObj,
        );
      };
      addToast({
        message: `'${entityStore.value[storeKey].name}' has been updated`,
        type: ToastType.SUCCESS,
        undoable: true,
        undoCallback,
      });
    } catch (err) {
      addToast({message: `Failed to update the description`, isError: true, type: ToastType.ERROR});
      this.updateEntityDescriptionAtStore(entityStore, storeKey, descriptionBeforeUpdate);
    }
  }

  updateCohortDescription(detail) {
    const cohortStoreKey = detail.item.id;
    const descriptionBeforeUpdate = (cohortsStore.value[cohortStoreKey] as any).description;
    const cohortPropertyUpdate = {description: detail.description};

    const updateRequestObj = {
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
      cohortID: detail.item.id,
      cohort: cohortPropertyUpdate,
    };
    const undoRequestObj = {...updateRequestObj, cohort: {description: descriptionBeforeUpdate}};
    this.updateEntityDescription(
      cohortsStore,
      detail.description,
      cohortStoreKey,
      updateRequestObj,
      undoRequestObj,
      `cohort property`,
    );
  }

  async updateEventDescription(detail) {
    const eventStoreKey = detail.item.name;
    const IdName = detail.item.type === EntityTypeNameSingular.Event ? `id` : `customEventId`;
    const descriptionBeforeUpdate = eventDefinitionsStore.value[eventStoreKey].description;

    let eventDefinition = {[IdName]: detail.item.id, description: detail.description};
    if (!detail.item.id) {
      eventDefinition = {name: detail.item.name, description: detail.description};
    }
    const updateRequestObj = {
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
      eventDefinition,
    };

    const undoRequestObj = {
      ...updateRequestObj,
      eventDefinition: {...updateRequestObj.eventDefinition, description: descriptionBeforeUpdate},
    };

    const count = eventDefinitionsStore.value[eventStoreKey].count;
    await this.updateEntityDescription(
      eventDefinitionsStore,
      detail.description,
      eventStoreKey,
      updateRequestObj,
      undoRequestObj,
      detail.item.type,
    );
    eventDefinitionsStore.update({
      [eventStoreKey]: {
        ...eventDefinitionsStore.value[eventStoreKey],
        count,
      },
    });
  }

  updatePropertyDescription(detail) {
    const propertyStoreKey = detail.item.name;
    const propertyStore =
      detail.item.type === EntityTypeNameSingular.EventProperty
        ? eventPropertyDefinitionsStore
        : peoplePropertyDefinitionsStore;

    const descriptionBeforeUpdate = propertyStore.value[propertyStoreKey].description;

    let propertyDefinition;

    if (detail.item.id) {
      propertyDefinition = {id: detail.item.id, description: detail.description};
    } else {
      propertyDefinition = {
        name: detail.item.name,
        resourceType: detail.item.type === EntityTypeNameSingular.EventProperty ? `Event` : `User`,
        description: detail.description,
      };
    }

    const updateRequestObj = {
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
      propertyDefinition,
    };

    const undoRequestObj = {
      ...updateRequestObj,
      propertyDefinition: {...updateRequestObj.propertyDefinition, description: descriptionBeforeUpdate},
    };

    this.updateEntityDescription(
      propertyStore,
      detail.description,
      propertyStoreKey,
      updateRequestObj,
      undoRequestObj,
      detail.item.type === EntityTypeNameSingular.EventProperty ? `event property` : `user property`,
    );
  }

  async updateCustomPropertyDescription(detail) {
    const customPropertyId = detail.item.id;

    const customProperty = Object.assign(
      {},
      {...customPropertiesStore.value[customPropertyId], description: detail.description},
    );
    const oldDescription = customPropertiesStore.value[customPropertyId].description;
    const cancelable = false;
    this.track(
      TRACKING_EVENTS.EDIT_DESCRIPTION,
      {
        'New Description': detail.description,
        'old description': oldDescription,
        'access point': `context panel`,
        'Entity Type': `custom property`,
      },
      cancelable,
    );
    try {
      await customPropertiesStore.put(customPropertyId, {
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
        customPropertyId,
        customProperty,
      });
      const undoCallback = async () => {
        await customPropertiesStore.put(customPropertyId, {
          projectId: this.attr(`project-id`),
          workspaceId: this.attr(`workspace-id`),
          customPropertyId,
          customProperty: {...customProperty, description: oldDescription},
        });
      };
      addToast({
        message: `'${detail.item.name}' has been updated`,
        type: ToastType.SUCCESS,
        undoable: true,
        undoCallback,
      });
    } catch (err) {
      addToast({message: `Failed to update the description`, type: ToastType.ERROR});
    }
  }

  // Delay closing the context panel so that when moving so that when moving the mouse from the
  // query block to the context panel, the context doesn't get closed.
  delayHideContextPanel() {
    this.clearContextPanelVisibilityTimer();
    this.contextPanelVisibilityTimerId = setTimeout(() => {
      if (this.state.isMenuOpen) {
        return;
      }
      this.update({tooltipData: null});
    }, 100);
  }

  set getSortedMenuSections(getSortedMenuSections) {
    this.update({getSortedMenuSections});
  }
}

export const QueryBlockEl = defineAndMakeJsxFactory(COMPONENT_NAME, QueryBlock);
